<script lang="ts">
	import '../app.css';
	import { onMount } from 'svelte';
	import { syncDriver } from '$lib/sync/driver.svelte';
	import { pruneOrphanImages, pruneSyncedLinkedImages, pruneImagesOverCap } from '$db/schema';
	import Toast from '$lib/components/Toast.svelte';

	let { children } = $props();

	let online = $state(true);

	onMount(() => {
		online = navigator.onLine;
		const onOnline = () => (online = true);
		const onOffline = () => (online = false);
		window.addEventListener('online', onOnline);
		window.addEventListener('offline', onOffline);

		// Start sync driver — deferred 2s internally so first paint isn't blocked.
		syncDriver.start();

		// v5.0.3 XR M-5 (CCQ-309): drop orphan image blobs older than 48h.
		// Keeps `db.images` bounded so IDB quota pressure doesn't evict our
		// entire origin (which would take items/events with it). Fire-and-
		// forget; failure is logged inside the helper.
		void pruneOrphanImages();

		// v5.0.9 CCQ-309 R2 M-1 (BEACON-13): prune synced-linked image rows
		// older than 7 days, then enforce the 200 MB last-resort byte cap.
		// Sequential (TTL first, then cap) so the cap rarely has to act.
		// Fire-and-forget; each helper logs its own failure.
		void (async () => {
			await pruneSyncedLinkedImages();
			await pruneImagesOverCap();
		})();

		return () => {
			window.removeEventListener('online', onOnline);
			window.removeEventListener('offline', onOffline);
			syncDriver.stop();
		};
	});
</script>

<!--
	v4.1.6 CER-21 ER-SLx5 (MED): skip link for keyboard + screen-reader users.
	First Tab lands on "Skip to content" which jumps focus into <main>, past
	the sticky chrome (offline chip) and page-level controls. Visible on
	keyboard focus only; invisible on mouse focus.
-->
<a class="skip-link" href="#main">Skip to content</a>

<div class="chrome" role="presentation">
	{#if !online}
		<div class="offline-chip" role="status" aria-live="polite">
			<span aria-hidden="true">●</span>
			<span>Offline</span>
		</div>
	{/if}
</div>

<main id="main" tabindex="-1">
	{@render children()}
</main>

<Toast />

<style>
	/* v4.1.6 CER-21 ER-SLx5: skip link. */
	.skip-link {
		position: absolute;
		top: 0;
		left: 0;
		transform: translateY(-200%);
		z-index: 100;
		padding: var(--space-3) var(--space-4);
		background: var(--color-accent-primary);
		color: var(--color-bg-primary);
		font-weight: 600;
		text-decoration: none;
		border-radius: 0 0 var(--radius-md) 0;
		transition: transform 0.15s ease-out;
	}
	.skip-link:focus,
	.skip-link:focus-visible {
		transform: translateY(0);
		outline: 2px solid var(--color-bg-primary);
		outline-offset: -4px;
	}
	/* Ensure main receives focus cleanly when skip-link target. */
	main:focus,
	main:focus-visible {
		outline: none;
	}

	.chrome {
		position: sticky;
		top: 0;
		z-index: 10;
		background: var(--color-bg-primary);
	}

	.offline-chip {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-2) var(--space-4);
		background: var(--color-bg-elevated);
		color: var(--color-warning);
		font-size: 14px;
		font-weight: 500;
		border-bottom: 1px solid var(--color-border-default);
	}

	.offline-chip span[aria-hidden] {
		color: var(--color-warning);
		font-size: 10px;
	}

	main {
		min-height: 100dvh;
		padding: var(--space-4);
		max-width: 720px;
		margin: 0 auto;
	}
</style>
